import { useState } from "react";
import { api, setToken } from "../lib/api";
import type { AppConfig, SessionInfo } from "../lib/types";
import Logo from "./Logo";
import { useI18n } from "../lib/i18n";

// The published demo-mode account (engine seeds it; README). Shown and filled only when the API reports demo mode.
const DEMO_USER = "demo.clinician";
const DEMO_PASSWORD = "pedob-demo-2026!";

export default function Login({
  config,
  notice,
  onSignedIn,
}: {
  config: AppConfig | null;
  notice: string | null;
  onSignedIn: (s: SessionInfo) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { t } = useI18n();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.login(username, password);
      setToken(r.token);
      setPassword("");
      onSignedIn(r.session);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message.replace(/^\d+: /, "") : String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "70vh" }}>
      <form className="card stack" onSubmit={submit} style={{ width: "100%", maxWidth: 380 }} aria-label="Sign in">
        <div style={{ textAlign: "center", padding: "6px 0 2px" }}>
          <Logo height={44} />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 20 }}>{t("signIn")}</h1>
          <p className="muted small" style={{ margin: "4px 0 0" }}>
            {t("loginSub")}
          </p>
        </div>
        {notice && <p className="banner" style={{ margin: 0, borderRadius: 8 }}>{notice}</p>}
        <label className="field">
          {t("username")}
          <input type="text" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
        </label>
        <label className="field">
          {t("password")}
          <span className="pw">
            <input type={showPw ? "text" : "password"} autoComplete="current-password" autoCapitalize="none"
              autoCorrect="off" spellCheck={false} value={password} onChange={(e) => setPassword(e.target.value)} />
            <button type="button" className="btn small" onClick={() => setShowPw(!showPw)} aria-pressed={showPw}>
              {showPw ? t("hidePassword") : t("showPassword")}
            </button>
          </span>
        </label>
        {err && <p className="error small" role="alert">{err}</p>}
        <button className="btn primary" disabled={busy || !username || !password}>
          {busy ? t("signingIn") : t("signIn")}
        </button>
        {config?.demo_mode && (
          <>
            {/* Quick fill for the shared clinician account only, as in the other Pulse demos: demo.admin has its own
                password on internet-reachable demos (PEDOB_DEMO_ADMIN_PASSWORD). dir="ltr" keeps the "!" at the
                end of the password in Arabic. */}
            <div className="stack" style={{ gap: 6 }}>
              <p className="muted small" style={{ margin: 0 }}>
                {t("demoAccount")}: <code dir="ltr" style={{ whiteSpace: "nowrap" }}>{DEMO_USER}</code> · {t("password")}{" "}
                <code dir="ltr" style={{ whiteSpace: "nowrap" }}>{DEMO_PASSWORD}</code>
              </p>
              <div className="row">
                <button type="button" className="chip" onClick={() => { setUsername(DEMO_USER); setPassword(DEMO_PASSWORD); setErr(null); }}>
                  {t("demoClinician")}
                </button>
              </div>
            </div>
            <p className="banner small" role="note" style={{ margin: 0, borderRadius: 8 }}>
              {t("syntheticNotice")}
            </p>
          </>
        )}
        <p className="muted small" style={{ margin: 0 }}>
          {t("loginEhr")}
        </p>
      </form>
    </div>
  );
}
